import {createAsyncThunk} from '@reduxjs/toolkit'

import {qs} from '@shared/helpers/queryString'

import {LocationChangeAction} from '@web/actions/application'
import {CARD, CARD_PAY, FOREIGN_SETTING} from '@web/core/urls'
import {unsubscribeMessage, subscribeMessage, changeBrowserLocation, openPopup} from '@web/helpers/browser'
import {examineUrlSafety} from '@web/helpers/path'
import {isMobile} from '@web/helpers/userAgent'
import {LanguageType} from '@web/i18n'
import {languageSelector} from '@web/slices/common/slice'

import {
    NavigationResult,
    NavigationTypeSelectableParam,
    PopupMessageReceiveParam,
    ReturnUrlComposerParam,
    ThunkApi,
} from '../interfaces'

/**
 * 간편 카드 등록화면 FE 이관 테스트로 인한 임시처리.
 * 2025 12월중 페이지 이관완료되면 `${CARD_PAY}/simplepay/registration` 으로 전체변경예정
 */
const isTestEnv = (process.env['REACT_APP_PROFILE'] || process.env['NEXT_PUBLIC_PROFILE']) === 'test'
const EASY_CARD_REGISTRATION_URL = isTestEnv
    ? `${CARD_PAY}/simplepay/registration`
    : `${CARD}/build/index.html#/card-registration`
const getForeignCardRegistrationUrl = (language: LanguageType) => `${FOREIGN_SETTING}/management/${language}/card`

const POPUP_KEY = 'REGISTER_EASY_CARD'
const MESSAGE_KEY = 'paymethod_registration_creditCard'

/**
 * 간편결제 카드 등록 페이지로 이동
 */
const navigateToEasyCardRegister = createAsyncThunk<
    NavigationResult,
    (NavigationTypeSelectableParam & PopupMessageReceiveParam & ReturnUrlComposerParam) | void,
    ThunkApi
>('navigateTo/easyCardRegister', async (args, {getState}) => {
    const {forceLocationChange = false, bridgeUrl = ''} = args ?? {}
    const state = getState()
    const shouldChangeLocation = forceLocationChange || isMobile
    const {language} = languageSelector(state)
    const isDomestic = language === 'ko'

    if (shouldChangeLocation) {
        const url = qs.stringifyUrl({
            url: isDomestic ? EASY_CARD_REGISTRATION_URL : getForeignCardRegistrationUrl(language),
            query: isDomestic
                ? {
                      goBackOnClose: true,
                      successUrl: window.location.href,
                      failUrl: window.location.href,
                  }
                : {
                      rurl: examineUrlSafety(window.location.href, ''),
                  },
        })

        changeBrowserLocation({url, type: LocationChangeAction.PUSH})
        // page 이동은 그 자체로 추가동작 없이 resolve 된다
        return Promise.resolve()
    }

    return new Promise<void | string>((resolve, reject) => {
        const resolveCallback = (result: {message: string}) => {
            if (result.message === 'success') {
                return resolve(result.message)
            }

            resolve()
        }

        // popup으로부터 유효한 메시지 받았을 때만 result로  메시지 전달
        subscribeMessage({messageKey: MESSAGE_KEY, subscriber: resolveCallback})

        // popup만 오픈
        openPopup({
            key: POPUP_KEY,
            url: isDomestic ? EASY_CARD_REGISTRATION_URL : getForeignCardRegistrationUrl(language),
            popupQuery: isDomestic
                ? {
                      successUrl: `${bridgeUrl}/${MESSAGE_KEY}/success`,
                      failUrl: `${bridgeUrl}/${MESSAGE_KEY}/fail`,
                  }
                : {
                      rurl: examineUrlSafety(`${bridgeUrl}/${MESSAGE_KEY}/success`, ''),
                  },
            title: '카드등록',
            size: {
                width: 500,
                height: 700,
            },
        })
            .then(() => resolve())
            .catch(reject)
            .finally(() => {
                // popup open 실패했든 종료했든 popup open시도가 끝났다면 message subscription 종료
                unsubscribeMessage({messageKey: MESSAGE_KEY, subscriber: resolveCallback})
            })
    })
})

export default navigateToEasyCardRegister
